import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import {
  CalendarDays,
  FileText,
  Receipt,
  TrendingUp,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { apiService } from '../../services/apiService';
import type { Appointment, InvoiceDocument } from '../../services/apiService';
import PageHeader from './ui/PageHeader';
import { fmtMoneyPlain } from './billing/invoiceHelpers';

type RangeKey = '7d' | '30d' | '90d' | 'ytd';

const RANGES: { key: RangeKey; label: string }[] = [
  { key: '7d', label: '7 T' },
  { key: '30d', label: '30 T' },
  { key: '90d', label: '90 T' },
  { key: 'ytd', label: 'YTD' },
];

function rangeBounds(key: RangeKey): { from: moment.Moment; to: moment.Moment } {
  const to = moment().endOf('day');
  let from: moment.Moment;
  switch (key) {
    case '7d':
      from = moment().subtract(6, 'days').startOf('day');
      break;
    case '30d':
      from = moment().subtract(29, 'days').startOf('day');
      break;
    case '90d':
      from = moment().subtract(89, 'days').startOf('day');
      break;
    case 'ytd':
      from = moment().startOf('year');
      break;
  }
  return { from, to };
}

interface KpiTileProps {
  icon: LucideIcon;
  label: string;
  value: string;
  subtitle?: string;
  tone: 'primary' | 'success' | 'warning' | 'info';
}

const TONE_CHIP: Record<KpiTileProps['tone'], string> = {
  primary: 'bg-brand-accent/15 text-brand-primary',
  success: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400',
  warning: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400',
  info: 'bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-400',
};

const KpiTile: React.FC<KpiTileProps> = ({ icon: Icon, label, value, subtitle, tone }) => (
  <div className="rounded-xl bg-brand-surface border border-brand-border p-5 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wider text-brand-text-muted">{label}</p>
        <p className="mt-2 text-3xl font-semibold tracking-tight text-brand-text leading-none tabular-nums">{value}</p>
        {subtitle && <p className="mt-1 text-[11px] text-brand-text-muted">{subtitle}</p>}
      </div>
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${TONE_CHIP[tone]}`}>
        <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
      </div>
    </div>
  </div>
);

// ---------- Time-series helpers ----------

type Bucket = {
  key: string;
  label: string;
  completed: number;
  planned: number;
  lost: number;
};

function buildBuckets(
  appts: Appointment[],
  from: moment.Moment,
  to: moment.Moment,
  range: RangeKey,
): Bucket[] {
  const useWeeks = range === '90d' || range === 'ytd';
  const buckets: Bucket[] = [];
  const idx = new Map<string, number>();

  const cursor = from.clone();
  while (cursor.isSameOrBefore(to, 'day')) {
    const k = useWeeks ? cursor.clone().startOf('isoWeek').format('YYYY-MM-DD') : cursor.format('YYYY-MM-DD');
    if (!idx.has(k)) {
      idx.set(k, buckets.length);
      const m = moment(k);
      const label = useWeeks ? `KW ${m.isoWeek()}` : m.format('DD.MM.');
      buckets.push({ key: k, label, completed: 0, planned: 0, lost: 0 });
    }
    cursor.add(useWeeks ? 1 : 1, useWeeks ? 'week' : 'day');
    if (useWeeks && cursor.isAfter(to, 'day')) break;
  }

  for (const a of appts) {
    const d = moment(a.appointment_date);
    if (!d.isValid() || d.isBefore(from, 'day') || d.isAfter(to, 'day')) continue;
    const k = useWeeks ? d.clone().startOf('isoWeek').format('YYYY-MM-DD') : d.format('YYYY-MM-DD');
    const i = idx.get(k);
    if (i === undefined) continue;
    if (a.status === 'completed') buckets[i].completed++;
    else if (a.status === 'cancelled' || a.status === 'no_show') buckets[i].lost++;
    else buckets[i].planned++;
  }

  return buckets;
}

const ChartLegend: React.FC = () => (
  <div className="flex items-center gap-4 text-[11px] text-brand-text-muted">
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-500" /> Abgeschlossen
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-2.5 w-2.5 rounded-sm bg-sky-500" /> Geplant
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-2.5 w-2.5 rounded-sm bg-rose-400" /> Ausgefallen
    </span>
  </div>
);

const TimeSeriesChart: React.FC<{ buckets: Bucket[] }> = ({ buckets }) => {
  const max = Math.max(1, ...buckets.map(b => b.completed + b.planned + b.lost));
  const H = 180;
  const PAD_TOP = 8;
  const PAD_BOTTOM = 22;
  const usable = H - PAD_TOP - PAD_BOTTOM;
  const n = buckets.length;
  if (n === 0) {
    return <div className="text-sm text-brand-text-muted py-8 text-center">Keine Termindaten im Zeitraum.</div>;
  }
  // Use viewBox so it scales with container width.
  const W = Math.max(n * 28, 320);
  const slot = W / n;
  const barW = Math.min(20, slot * 0.7);
  // Y-axis grid (4 lines)
  const grid = [0, 0.25, 0.5, 0.75, 1].map(t => Math.round(t * max));

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-[200px]">
        {/* gridlines + y labels */}
        {grid.map((g, i) => {
          const y = PAD_TOP + usable - (g / max) * usable;
          return (
            <g key={i}>
              <line x1={0} x2={W} y1={y} y2={y} stroke="currentColor" strokeWidth={0.5} className="text-brand-border" />
              <text x={2} y={y - 2} className="fill-current text-brand-text-muted" fontSize={9}>{g}</text>
            </g>
          );
        })}

        {buckets.map((b, i) => {
          const total = b.completed + b.planned + b.lost;
          const cx = i * slot + slot / 2;
          const x = cx - barW / 2;
          const hCompleted = (b.completed / max) * usable;
          const hPlanned = (b.planned / max) * usable;
          const hLost = (b.lost / max) * usable;
          let yCursor = PAD_TOP + usable;
          // completed bottom
          const yC = yCursor - hCompleted; yCursor = yC;
          const yP = yCursor - hPlanned; yCursor = yP;
          const yL = yCursor - hLost;
          const showLabel = n <= 31 || i % Math.ceil(n / 12) === 0;
          return (
            <g key={b.key}>
              {b.lost > 0 && (
                <rect x={x} y={yL} width={barW} height={hLost} className="fill-rose-400">
                  <title>{`${b.label}: ${b.lost} ausgefallen`}</title>
                </rect>
              )}
              {b.planned > 0 && (
                <rect x={x} y={yP} width={barW} height={hPlanned} className="fill-sky-500">
                  <title>{`${b.label}: ${b.planned} geplant`}</title>
                </rect>
              )}
              {b.completed > 0 && (
                <rect x={x} y={yC} width={barW} height={hCompleted} className="fill-emerald-500">
                  <title>{`${b.label}: ${b.completed} abgeschlossen`}</title>
                </rect>
              )}
              {total === 0 && (
                <rect x={x} y={PAD_TOP + usable - 1} width={barW} height={1} className="fill-brand-border" />
              )}
              {showLabel && (
                <text x={cx} y={H - 6} textAnchor="middle" className="fill-current text-brand-text-muted" fontSize={9}>
                  {b.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// ---------- Funnel ----------

type FunnelStage = { label: string; value: number; convFromPrev?: number };

const Funnel: React.FC<{ stages: FunnelStage[] }> = ({ stages }) => {
  const max = Math.max(1, ...stages.map(s => s.value));
  return (
    <div className="space-y-2">
      {stages.map((s, i) => {
        const pct = (s.value / max) * 100;
        return (
          <div key={s.label} className="flex items-center gap-3">
            <div className="w-40 shrink-0 text-xs text-brand-text-muted">{s.label}</div>
            <div className="flex-1 relative h-7 rounded-md bg-brand-surface-2 overflow-hidden">
              <div
                className="absolute left-0 top-0 h-full bg-brand-accent/70 transition-all"
                style={{ width: `${pct}%` }}
                aria-hidden
              />
              <div className="absolute inset-0 flex items-center px-2.5 text-xs font-semibold text-brand-text tabular-nums">
                {s.value}
              </div>
            </div>
            <div className="w-12 shrink-0 text-right text-xs text-brand-text-muted tabular-nums">
              {i === 0 ? '' : s.convFromPrev !== undefined ? `${Math.round(s.convFromPrev * 100)}%` : ''}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------- Page ----------

const ReportsPage: React.FC = () => {
  const [range, setRange] = useState<RangeKey>('30d');
  const [appts, setAppts] = useState<Appointment[]>([]);
  const [invoices, setInvoices] = useState<InvoiceDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { from, to } = useMemo(() => rangeBounds(range), [range]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const fromIso = from.format('YYYY-MM-DD');
        const toIso = to.format('YYYY-MM-DD');
        const [apptResp, invResp] = await Promise.allSettled([
          apiService.getAppointments(),
          apiService.listInvoices({ from: fromIso, to: toIso }),
        ]);
        if (cancelled) return;
        setAppts(apptResp.status === 'fulfilled' ? (apptResp.value.data.data || []) : []);
        setInvoices(invResp.status === 'fulfilled' ? (invResp.value.data.data || []) : []);
      } catch (e: any) {
        if (!cancelled) setError('Auswertungen konnten nicht geladen werden.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [from, to]);

  // Filter appointments to range (client-side)
  const apptsInRange = useMemo(
    () =>
      appts.filter(a => {
        const d = moment(a.appointment_date);
        return d.isValid() && d.isSameOrAfter(from, 'day') && d.isSameOrBefore(to, 'day');
      }),
    [appts, from, to],
  );

  const buckets = useMemo(() => buildBuckets(apptsInRange, from, to, range), [apptsInRange, from, to, range]);

  // KPIs
  const totalAppts = apptsInRange.length;
  const offers = invoices.filter(d => d.type === 'offer');
  const invoiceDocs = invoices.filter(d => d.type === 'invoice');
  const offersSum = offers.reduce((s, d) => s + (d.total_cents || 0), 0);
  const invoicesSum = invoiceDocs.reduce((s, d) => s + (d.total_cents || 0), 0);
  const conv = offers.length === 0 ? 0 : invoiceDocs.length / offers.length;

  // Funnel
  const apptConfirmed = apptsInRange.filter(a => a.status === 'confirmed' || a.status === 'completed' || a.status === 'scheduled').length;
  const apptCompleted = apptsInRange.filter(a => a.status === 'completed').length;
  const funnelStages: FunnelStage[] = [
    { label: 'Termine gebucht', value: totalAppts },
    { label: 'Bestätigt / geplant', value: apptConfirmed, convFromPrev: totalAppts ? apptConfirmed / totalAppts : 0 },
    { label: 'Abgeschlossen', value: apptCompleted, convFromPrev: apptConfirmed ? apptCompleted / apptConfirmed : 0 },
    { label: 'Angebot erstellt', value: offers.length, convFromPrev: apptCompleted ? offers.length / apptCompleted : 0 },
    { label: 'Rechnung gestellt', value: invoiceDocs.length, convFromPrev: offers.length ? invoiceDocs.length / offers.length : 0 },
  ];

  // Top procedures (by completed appointments)
  const topProcedures = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of apptsInRange) {
      if (a.status !== 'completed') continue;
      const name = a.procedure_name || '–';
      m.set(name, (m.get(name) || 0) + 1);
    }
    return Array.from(m.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [apptsInRange]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Praxis"
        title="Auswertungen"
        description="Termine, Angebote und Rechnungen im Überblick"
        actions={
          <div className="inline-flex rounded-md border border-brand-border bg-brand-surface overflow-hidden">
            {RANGES.map(r => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  range === r.key
                    ? 'bg-brand-primary text-white'
                    : 'text-brand-text hover:bg-brand-surface-2'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        }
      />

      {error && (
        <div className="rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 px-4 py-3">
          <p className="text-sm font-medium text-rose-800 dark:text-rose-300">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-border border-t-brand-primary" />
        </div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiTile
              icon={CalendarDays}
              label="Termine"
              value={String(totalAppts)}
              subtitle={`${apptCompleted} abgeschlossen`}
              tone="primary"
            />
            <KpiTile
              icon={FileText}
              label="Angebote"
              value={String(offers.length)}
              subtitle={`Σ ${fmtMoneyPlain(offersSum)} €`}
              tone="info"
            />
            <KpiTile
              icon={Receipt}
              label="Rechnungen"
              value={String(invoiceDocs.length)}
              subtitle={`Σ ${fmtMoneyPlain(invoicesSum)} €`}
              tone="success"
            />
            <KpiTile
              icon={TrendingUp}
              label="Angebot → Rechnung"
              value={`${Math.round(conv * 100)}%`}
              subtitle={offers.length === 0 ? 'keine Angebote im Zeitraum' : `${invoiceDocs.length} / ${offers.length}`}
              tone="warning"
            />
          </div>

          {/* Time-series */}
          <section className="rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
            <header className="flex items-center justify-between px-6 py-4 border-b border-brand-border">
              <div>
                <h2 className="text-sm font-semibold text-brand-text">Termine im Zeitverlauf</h2>
                <p className="text-xs text-brand-text-muted mt-0.5">
                  {from.format('DD.MM.YYYY')} – {to.format('DD.MM.YYYY')}
                </p>
              </div>
              <ChartLegend />
            </header>
            <div className="px-6 py-5">
              <TimeSeriesChart buckets={buckets} />
            </div>
          </section>

          {/* Funnel + top procedures */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <section className="lg:col-span-2 rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
              <header className="px-6 py-4 border-b border-brand-border">
                <h2 className="text-sm font-semibold text-brand-text">Patienten-Flow</h2>
                <p className="text-xs text-brand-text-muted mt-0.5">Vom Termin zur Rechnung — Conversion zwischen den Stufen</p>
              </header>
              <div className="px-6 py-5">
                <Funnel stages={funnelStages} />
              </div>
            </section>

            <section className="rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
              <header className="px-6 py-4 border-b border-brand-border">
                <h2 className="text-sm font-semibold text-brand-text">Top Behandlungen</h2>
                <p className="text-xs text-brand-text-muted mt-0.5">Abgeschlossen im Zeitraum</p>
              </header>
              <ul className="divide-y divide-brand-border">
                {topProcedures.length === 0 ? (
                  <li className="px-6 py-8 text-center text-sm text-brand-text-muted">Keine abgeschlossenen Termine.</li>
                ) : (
                  topProcedures.map(([name, count]) => (
                    <li key={name} className="px-6 py-3 flex items-center justify-between gap-3">
                      <span className="text-sm text-brand-text truncate">{name}</span>
                      <span className="text-sm font-semibold text-brand-text tabular-nums">{count}</span>
                    </li>
                  ))
                )}
              </ul>
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportsPage;
